<script lang="ts">
  import type { Waypoint } from "../../support/connections";
  import { constrain, containsPoint, constrainPoint, slope } from "../../support/geometry";

  /**
   * Observable location of user, contains the following fields:
   * x - x pixel coordinate on map
   * y - y pixel coordinate on map
   * gravity - size of user's gravity well
   * type - waypoint type
   */
  export let fence = { x: 0, y: 0, width: Infinity, height: Infinity };
  export let location: Waypoint;
  export let transition = true;
  export let zoom = 1;

  $: point = constrainPoint(location, fence);
  $: scale = constrain(1 / zoom, 0.7, 4);
  $: isOutside = containsPoint(location, fence) == false;
  $: angle = slope({ x: fence.width * 0.5, y: fence.height * 0.5 }, point);
</script>

{#if location}
  <svg
    class:transition
    viewBox="0 0 60 60"
    width="60"
    height="60"
    style="--x: {point.x - 30}px; --y: {point.y - 30}px"
  >
    <defs>
      <radialGradient id="shadowGradient">
        <stop offset="0" stop-color="rgba(0, 0, 0, 0.2)" />
        <stop offset="70%" stop-color="transparent" />
      </radialGradient>

      <marker
        id="marker-arrow"
        viewBox="0 0 10 10"
        refX="5"
        refY="5"
        markerWidth="3"
        markerHeight="3"
        orient="auto-start-reverse"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--connection-color)" />
      </marker>
    </defs>

    {#if isOutside}
      <line
        class="Direction"
        x1={30 + Math.cos(angle) * -40}
        y1={30 + Math.sin(angle) * -40}
        x2={30 + Math.cos(angle) * -20}
        y2={30 + Math.sin(angle) * -20}
        stroke="var(--connection-color)"
        stroke-linecap="round"
        stroke-width="3"
        marker-end="url(#marker-arrow)"
      />
    {/if}

    <circle
      class="Gravity"
      cx="50%"
      cy="50%"
      r={location.gravity * scale}
      stroke-width={1 * scale}
    />

    <circle fill="url(#shadowGradient)" cx="50%" cy="50%" r={location.gravity * scale} />

    <circle class="Marker" cx="50%" cy="50%" r={scale * 7} stroke-width={2 * scale} />

    {#each [0, 0.6, 0.9] as timing}
      <circle
        class="Ripple"
        r={scale * 30}
        cx="50%"
        cy="50%"
        style="animation-delay: {timing}s;"
        stroke-width={2 * scale}
      />
    {/each}
  </svg>
{/if}

<style>
  @keyframes animate-pool {
    0% {
      opacity: 0.7;
      transform: scale(0);
    }

    90% {
      opacity: 0;
    }

    100% {
      transform: scale(1);
    }
  }

  @keyframes pop-in {
    0% {
      opacity: 0;
      transform: scale(0);
    }

    90% {
      opacity: 1;
      transform: scale(1.1);
    }

    100% {
      transform: scale(1);
    }
  }

  svg {
    animation: 0.5s pop-in ease-in;
    position: absolute;
    top: var(--y, 0);
    left: var(--x, 0);
    overflow: visible;
  }

  svg.transition {
    transition:
      0.2s top linear,
      0.2s left linear;
  }

  circle {
    transform-origin: 50% 50%;
    will-change: transform;
  }

  .Gravity {
    fill-opacity: 0.15;
    fill: var(--connection-color);
    stroke: var(--connection-color);
    stroke-opacity: 0.1;
  }

  .Marker {
    fill-opacity: 0.8;
    fill: var(--connection-color);
    stroke: white;
    filter: drop-shadow(0 0 0 2px #000000);
  }

  .Ripple {
    animation: 5s animate-pool 2s;
    animation-iteration-count: infinite;
    animation-timing-function: cubic-bezier(0.02, 1.01, 0.43, 1.26);
    fill: transparent;
    opacity: 0;
    stroke: var(--connection-color);
  }
</style>
